import { Injectable } from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {FormsSearch} from '../models/forms-search.interface';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {Forms} from '../models/forms.interface';
import {environment} from '../../../environments/environment';
import {Answer} from '../models/answer.interface';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {AnswerForm} from '../models/answer-form.interface';
import {FormsAdmin} from '../../intelligence-reporting/models/forms.interface';
import {FormsAdminSearch} from '../../intelligence-reporting/models/forms-search.interface';
import {QuestionsWithLastYearQuestions} from '../models/questions-with-last-year-answers.interface';
import {MessageService} from '../../shared/services/message.service';

const FORMS_API = environment.apiUrl + 'intelligencedata/answerforms';

@Injectable({
  providedIn: 'root'
})
export class FormsService {

  constructor(private http: HttpClient, private messageService: MessageService) { }

  static buildSearchParams(search: FormsSearch): string {
    let searchParams = '';
    if (search.userId) {
      searchParams += `&userId=${search.userId}`;
    }
    if (search.startYear) {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear) {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.form && search.form !== '') {
      searchParams += `&form=${search.form}`;
    }
    if (search.startLaunched && search.startLaunched !== '') {
      searchParams += `&startLaunched=${search.startLaunched}`;
    }
    if (search.endLaunched && search.endLaunched !== '') {
      searchParams += `&endLaunched=${search.endLaunched}`;
    }
    if (search.minQuestions) {
      searchParams += `&minQuestions=${search.minQuestions}`;
    }
    if (search.maxQuestions) {
      searchParams += `&maxQuestions=${search.maxQuestions}`;
    }
    if (search.formType && search.formType !== '') {
      searchParams += `&formType=${search.formType}`;
    }
    if (search.status && search.status !== '') {
      searchParams += `&status=${search.status}`;
    }
    return searchParams;
  }

  static buildSearchParamsAdmin(search: FormsAdminSearch): string {
    let searchParams = '';
    if (search.level && search.level !== '') {
      searchParams += `&level=${search.level}`;
    }
    if (search.name && search.name !== '') {
      searchParams += `&name=${search.name}`;
    }
    if (search.startYear) {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear) {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.form && search.form !== '') {
      searchParams += `&form=${search.form}`;
    }
    if (search.startLaunched && search.startLaunched !== '') {
      searchParams += `&startLaunched=${search.startLaunched}`;
    }
    if (search.endLaunched && search.endLaunched !== '') {
      searchParams += `&endLaunched=${search.endLaunched}`;
    }
    if (search.minQuestions) {
      searchParams += `&minQuestions=${search.minQuestions}`;
    }
    if (search.maxQuestions) {
      searchParams += `&maxQuestions=${search.maxQuestions}`;
    }
    if (search.status && search.status !== '') {
      searchParams += `&status=${search.status}`;
    }
    return searchParams;
  }


  static getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }

  getAll(search: FormsSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Forms>> {
    let url = `${FORMS_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = FormsService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${FormsService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Forms>>(url + searchParams);
  }

  getAllAnswerForms(search: FormsAdminSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<FormsAdmin>> {
    let url = `${FORMS_API}/forms?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = FormsService.buildSearchParamsAdmin(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${FormsService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<FormsAdmin>>(url + searchParams);
  }

  getForm(id: number, country: boolean, userId: number): Observable<Forms> {
    return this.http.get<Forms>(`${FORMS_API}/${id}?country=${country}&userId=${userId}`);
  }

  getFormQuestions(id: number, formType: string, countryId?: number, weeeSystemId?: number): Observable<QuestionsWithLastYearQuestions> {
    let url = `${FORMS_API}/questions?id=${id}&formType=${formType}`;
    if (countryId) {
      url += `&countryId=${countryId}`;
    }
    if (weeeSystemId) {
      url += `&weeeSystemId=${weeeSystemId}`;
    }
    return this.http.get<QuestionsWithLastYearQuestions>(url);
  }

  getFormAnswers(id: number, year: number, countryId?: number, weeeSystemId?: number): Observable<Answer[]> {
    let url = `${FORMS_API}/answers?id=${id}&year=${year}`;
    if (countryId) {
      url += `&countryId=${countryId}`;
    }
    if (weeeSystemId) {
      url += `&weeeSystemId=${weeeSystemId}`;
    }
    return this.http.get<Answer[]>(url);
  }

  saveAnswerForm(roundOfQuestionsId: number, countryId?: number, weeeSystemId?: number): Observable<ApiHttpResponse<AnswerForm>> {
    let url = `${FORMS_API}/answerform?roundOfQuestionsId=${roundOfQuestionsId}`;
    if (countryId) {
      url += `&countryId=${countryId}`;
    }
    if (weeeSystemId) {
      url += `&weeeSystemId=${weeeSystemId}`;
    }
    return this.http.post<ApiHttpResponse<AnswerForm>>(url, {})
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<AnswerForm>) => {
          return res;
        })
      );
  }

  saveAnswers(answers: Answer[]): Observable<ApiHttpResponse<Answer[]>> {
    return this.http.post<ApiHttpResponse<Answer[]>>(FORMS_API, answers)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Answer[]>) => {
          return res;
        })
      );
  }

  markAsCompleted(roundOfQuestionsId: number, year: number, countryId?: number, weeeSystemId?: number):
    Observable<ApiHttpResponse<AnswerForm>> {
    let url = `${FORMS_API}/?roundOfQuestionsId=${roundOfQuestionsId}&year=${year}`;
    if (countryId) {
      url += `&countryId=${countryId}`;
    }
    if (weeeSystemId) {
      url += `&weeeSystemId=${weeeSystemId}`;
    }
    return this.http.put<ApiHttpResponse<AnswerForm>>(url, {})
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<AnswerForm>) => {
          return res;
        })
      );
  }

  uploadAnswers(file: File): Observable<HttpResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<HttpResponse<any>>(`${FORMS_API}/upload`, formData)
      .pipe(
        catchError(err => {
          this.messageService.showMessage(err.error, 'error');
          return throwError(err);
        }),
        map((res: HttpResponse<any>) => {
          return res;
        })
      );
  }
}
